import styled, { css } from 'styled-components';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import { Box } from '@/components/Box';
import { MediaWrapper } from '@/components/Cms/MediaWrapper';
import { Container } from '@/components/Container';
import { LegacyButton as BaseButton } from '@/components/LegacyButton';
import { Typography } from '@/components/Typography';
import type { Maybe } from '@/lib/types';

import { ReactComponent as Calendar } from '@/assets/icons/calendar.svg';
import { ReactComponent as ChevronSvg } from '@/assets/icons/chevron.svg';
import { ReactComponent as Location } from '@/assets/icons/locationWhite.svg';
import { ReactComponent as PhoneOutline } from '@/assets/icons/phone-outline.svg';

export const StyledExhibitionImage = styled(AspectRatioImage)`
  object-fit: cover;
  width: 100%;
  aspect-ratio: 42 / 25;
`;

export const StyledContainer = styled(Container)`
  position: relative;
  overflow: hidden;
`;
export const ExhibitionOuter = styled(Box)`
  ${({ theme }) => css`
    padding-left: ${theme.spacing(2.375)};
    margin: auto;
    position: relative;
    ${theme.media.md.up} {
      padding-left: ${theme.spacing(0)};
      position: relative;
      display: flex;
      flex-direction: column;
      margin: 0 auto;
      width: 1105px;
    }
  `};
`;
export const ExhibitionWraper = styled(Box)<{
  $imagePosition: Maybe<string>;
}>`
  ${({ $imagePosition, theme }) => css`
    align-content: start;
    display: flex;
    padding-bottom: ${theme.spacing(0)};
    flex-direction: column-reverse;
    &::before {
      content: '';
      width: 12px;
      height: 12px;
      background-color: ${theme.colors.primary};
      border-radius: 50%;
      position: absolute;
      top: 14px;
      left: 1px;
      transform: translateX(-50%);
      z-index: 2;
    }
    &::after {
      border-left: 1px solid ${theme.colors.primary};
      bottom: 0;
      content: '';
      left: 0;
      position: absolute;
      top: 0;
      z-index: 1;
    }
    ${theme.media.md.up} {
      padding-bottom: ${theme.spacing(4.25)};
      flex-direction: ${$imagePosition === 'left' ? 'row' : 'row-reverse'};
      &::before {
        content: '';
        width: 12px;
        height: 12px;
        background-color: ${theme.colors.goldenRod};
        border-radius: 50%;
        position: absolute;
        top: 10px;
        left: ${$imagePosition === 'right' ? 'calc(50% + 0.2px)' : 'auto'};
        right: ${$imagePosition === 'left' ? 'calc(50% - 12.3px)' : 'auto'};
        transform: translateX(-50%);
        z-index: 1;
      }
      &::after {
        border-left: 1px solid ${theme.colors.goldenRod};
        bottom: 0;
        content: '';
        left: ${$imagePosition === 'right' ? 'calc(50% - 0.4px)' : 'auto'};
        right: ${$imagePosition === 'left' ? 'calc(50% - 0.4px)' : 'auto'};
        position: absolute;
        top: 0;
        z-index: 1;
      }
    }
  `};
`;
export const ExhibitionImageWraper = styled(Box)<{
  $imagePosition: Maybe<string>;
}>`
  ${({ $imagePosition, theme }) => css`
    display: block;
    overflow: hidden;
    margin: 10px auto 80px;
    margin-top: 0;
    max-width: 100%;
    position: relative;
    z-index: 2;
    width: 100%;
    margin-bottom: ${theme.spacing(2.5)};
    ${theme.media.md.up} {
      display: block;
      position: relative;
      z-index: 2;
      height: 310px;
      max-width: 640px;
      margin-top: ${theme.spacing(0)};
      margin-left: ${$imagePosition === 'left'
        ? theme.spacing(0)
        : theme.spacing(4)};
      margin-right: ${$imagePosition === 'left'
        ? theme.spacing(4)
        : theme.spacing(0)};
    }
  `};
`;
export const ExhibitionCard = styled(Box)<{
  $imagePosition: Maybe<string>;
}>`
  display: block;
  position: relative;
  z-index: 2;
  ${({ $imagePosition, theme }) => css`
    margin: 10px auto 80px;
    max-width: 100%;
    width: 100%;
    margin-bottom: ${theme.spacing(2.5)};
    ${theme.media.md.up} {
      height: 300px;
      max-width: 640px;
      margin-top: ${theme.spacing(0)};
      margin-bottom: ${theme.spacing(2.5)};
      margin-left: ${$imagePosition === 'left'
        ? theme.spacing(4)
        : theme.spacing(0)};
      margin-right: ${$imagePosition === 'left'
        ? theme.spacing(0)
        : theme.spacing(4)};
    }
  `};
`;
export const ExhibitionCardContent = styled(Box)`
  ${({ theme }) => css`
    border-top: 0;
    width: 100%;
    ${theme.media.md.up} {
      border-top: 0;
      display: grid;
      grid-template-rows: min-content auto min-content;
      width: 520px;
      height: 315px;
    }
  `};
`;
export const TitlesWrapper = styled(Box)`
  font-weight: 600;
`;
export const CityTitle = styled(Typography)<{ $imagePosition: Maybe<string> }>`
  ${({ $imagePosition, theme }) => css`
    line-height: ${theme.lineHeights.p1};
    text-align: left;
    font-size: ${theme.fontSizes.h4Mobile};
    ${theme.media.md.up} {
      line-height: 2.125rem;
      text-align: ${$imagePosition === 'left' ? 'left' : 'right'};
      font-size: ${theme.fontSizes.h3};
    }
  `};
`;
export const DateTitle = styled(Typography)<{ $imagePosition: Maybe<string> }>`
  ${({ $imagePosition, theme }) => css`
    margin-bottom: ${theme.spacing(2)};
    text-align: left;
    font-size: ${theme.fontSizes.h5Mobile};
    line-height: ${theme.lineHeights.p1};
    ${theme.media.md.up} {
      text-align: ${$imagePosition === 'left' ? 'left' : 'right'};
      font-size: ${theme.fontSizes.h4};
    }
  `};
`;
export const BoutiqueDetailWrapper = styled(Box)`
  ${({ theme }) => css`
    background: ${theme.colors.secondaryLightGrey};
  `};
`;
export const BoutiqueDetailInnerWrapper = styled(Box)`
  ${({ theme }) => css`
    padding: ${theme.spacing(2)};
  `};
`;
export const BoutiqueName = styled(Typography)`
  ${({ theme }) => css`
    font-weight: 600;
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.h5Mobile};
    ${theme.media.md.up} {
      font-size: ${theme.fontSizes.h4};
    }
  `};
`;
export const LocationIcon = styled(Location)`
  ${({ theme }) => css`
    & > path {
      fill: ${theme.colors.black};
    }
    margin-right: ${theme.spacing(0.5)};
  `}
  width: 19px;
  height: 19px;
  transform: translateY(3px);
`;
export const PhoneOutlineIcon = styled(PhoneOutline)`
  ${({ theme }) => css`
    & > path {
      fill: ${theme.colors.black};
    }
    margin-right: ${theme.spacing(0.5)};
  `}
  width: 19px;
  height: 19px;
  transform: translateY(3px);
`;
export const ContactLine = styled(Typography)`
  line-height: 22px;
  ${({ theme }) => css`
    margin: ${theme.spacing(1.25, 0)};
    font-family: ${theme.fontFamilies.openSans};
    font-size: ${theme.fontSizes.p2};
    color: ${theme.colors.default};
  `};
`;
export const ExhibitionCtaWrapper = styled(Box)`
  ${({ theme }) => css`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    padding-top: ${theme.spacing(1.25)};
    ${theme.media.md.up} {
      max-height: 55px;
      padding-top: ${theme.spacing(2.5)};
    }
  `};
`;
export const ExhibitionWrapper = styled(Box)`
  ${({ theme }) => css`
    color: ${theme.colors.default};
    background: ${theme.colors.primary};
    font-size: ${theme.fontSizes.h6};
  `};
  display: flex;
  line-height: 22px;
  padding: 10px 10px 5px 15px;
`;

export const ExhibitionPeriod = styled(Box)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p1};
    font-family: ${theme.fontFamilies.italianPlate};
  `};
`;
export const ExhibitionPeriodSpan = styled.span`
  ${({ theme }) => css`
    font-weight: ${theme.fontWeights.bold};
  `};
`;
export const EmailDirectionLinksWrapper = styled(Box)``;
export const LinksWrapper = styled(Box)`
  display: flex;
  ${({ theme }) => css`
    justify-content: unset;
    ${theme.media.md.up} {
      justify-content: space-between;
    }
  `};
  width: 100%;
`;
export const BoutiqueLink = styled(BaseButton)`
  text-decoration: underline;
  text-underline-offset: 5px;
  ${({ theme }) => css`
    text-decoration-color: ${theme.colors.primary};
    &:hover,
    &:focus {
      color: ${theme.colors.primaryDark};
    }
  `};
`;
export const AppointmentCtaDeskTop = styled(BaseButton)`
  ${({ theme }) => css`
    display: none;
    ${theme.media.md.up} {
      &:hover,
      &:focus {
        background-color: ${theme.colors.primaryDark};
        color: ${theme.colors.white};
        border-color: ${theme.colors.primaryDark};
      }
      height: 40px;
      font-size: ${theme.fontSizes.p3};
      display: block;
      width: 212px;
    }
  `};
`;

export const AppointmentCtaMobileWrapper = styled.div`
  width: 100%;
`;
export const AppointmentCtaMobile = styled(BaseButton)`
  ${({ theme }) => css`
    display: block;
    width: 100%;
    &:hover,
    &:focus {
      background-color: ${theme.colors.primaryDark};
      color: ${theme.colors.white};
      border-color: ${theme.colors.primaryDark};
    }
    height: 40px;
    font-size: ${theme.fontSizes.p3};
    ${theme.media.md.up} {
      display: none;
    }
  `};
`;
export const Chevron = styled(ChevronSvg)`
  width: 24px;
  fill: ${({ theme }) => theme.colors.primaryDark};
`;
export const CalendarIcon = styled(Calendar)`
  ${({ theme }) => css`
    margin-right: ${theme.spacing(1)};
    transform: translateY(-2px);
    height: 22px;
    width: 22px;
    g {
      fill: ${theme.colors.default};
    }
  `}
`;
export const StyledMediaWrapper = styled(MediaWrapper)``;
